import axios from "axios";
import { getLiveCurrencySnapshot, CURRENCY_META } from "@/context/CurrencyContext";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BASE}/api`;

// Auth is delivered exclusively via the httpOnly `access_token` cookie
// (or the Google `session_token` cookie). `withCredentials: true` lets the
// browser attach those cookies on every same-origin API call — the token
// is NEVER exposed to JavaScript, closing the localStorage XSS vector.
export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

// CSRF double-submit-cookie echo. The backend sets `md_csrf` on every safe
// response (non-httpOnly, readable from JS). We copy it into the
// X-CSRF-Token header on every state-changing request so the backend can
// verify the pair matches. Bootstrap endpoints (login, register, contact) are
// exempt on the server side; we still send the header if we have it — the
// server tolerates it.
function readCookie(name) {
    if (typeof document === "undefined") return null;
    const raw = document.cookie || "";
    for (const chunk of raw.split(";")) {
        const [k, ...v] = chunk.trim().split("=");
        if (k === name) return decodeURIComponent(v.join("="));
    }
    return null;
}

api.interceptors.request.use((config) => {
    const method = (config.method || "get").toUpperCase();
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
        const token = readCookie("md_csrf");
        if (token) {
            config.headers["X-CSRF-Token"] = token;
        }
    }
    return config;
});

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" · ");
    if (detail && typeof detail === "object") {
        // Backend returns structured details like `{reason, cap, current_open}` or
        // `{reason, next_action, held_qty}` — surface the reason first.
        if (typeof detail.reason === "string") return detail.reason;
        if (typeof detail.message === "string") return detail.message;
        if (typeof detail.msg === "string") return detail.msg;
        // Last-ditch: return a compact JSON string so we never leak `[object Object]`.
        try { return JSON.stringify(detail); } catch { return "Request failed."; }
    }
    return String(detail);
}

// Currency-aware money formatter — reads the CURRENT global currency from
// the CurrencyContext singleton, so every plain (non-hook) call site keeps
// following the header currency toggle automatically. `n` is always a
// KES-denominated amount (the app's internal base currency).
export function fmtKES(n) {
    if (n === null || n === undefined || isNaN(n)) return "—";
    const { currency, rates } = getLiveCurrencySnapshot();
    const num = Number(n);
    const converted = currency === "KES" ? num : num / (rates[currency] || 1);
    const sign = converted < 0 ? "-" : "";
    const sym = CURRENCY_META[currency]?.symbol || currency;
    const val = Math.abs(converted).toLocaleString("en-US", { maximumFractionDigits: 2 });
    return `${sign}${sym} ${val}`;
}

export function fmtPct(n, digits = 2) {
    if (n === null || n === undefined || isNaN(n)) return "—";
    return `${Number(n).toFixed(digits)}%`;
}

/**
 * placeBuyWithReview — Sentient Desk (Directive 1) helper for secondary buy
 * surfaces (Options chain, Onboarding demo trade) that don't show the rich
 * Kimani/Obondo modal UI. Still runs every order through the mandatory
 * review -> token -> place pipeline server-side; it just doesn't render the
 * persona card. Primary surfaces (PlaceATrade) use <AiOrderReviewModal/>
 * directly for the full interactive experience.
 */
export async function placeBuyWithReview({ symbol, side = "buy", qty, accountId, stopLossKes, takeProfitKes, rationales }) {
    const { data: review } = await api.post("/orders/review", {
        symbol, side, qty: Number(qty), account_id: accountId,
        stop_loss_kes: stopLossKes ? Number(stopLossKes) : undefined,
        take_profit_kes: takeProfitKes ? Number(takeProfitKes) : undefined,
    });
    if (review.pre_check_error || !review.review_token) {
        throw new Error(review.pre_check_error || "This order can't be reviewed right now.");
    }
    const payload = { symbol, side, qty: Number(qty), account_id: accountId, review_token: review.review_token };
    if (stopLossKes) payload.stop_loss_kes = Number(stopLossKes);
    if (takeProfitKes) payload.take_profit_kes = Number(takeProfitKes);
    if (rationales?.length) payload.rationales = rationales;
    return api.post("/orders", payload);
}
